/**
 * Pure tree logic (`060-notebook-tree.md`). No React, no queries — so the ordering, the
 * cycle check and the drop-target maths can be unit-tested without mounting anything.
 *
 * The API returns a FLAT array precisely so that a move is a one-field patch to one item
 * (`040-api-contract.md`). We build the hierarchy here, on the client, from `parentId` +
 * `position` — and `position` sorts by plain byte comparison, which is what
 * `fractional-indexing` guarantees and what Postgres's `COLLATE "C"` reproduces.
 */
import { generateKeyBetween } from 'fractional-indexing';
import type { TreePage } from '@/api/endpoints/pages';

export interface VisibleRow {
  page: TreePage;
  depth: number;
  hasChildren: boolean;
}

/** Byte order, not locale order. `'A' < 'a'`, and that is load-bearing (`060` §1). */
function byPosition(a: TreePage, b: TreePage): number {
  return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
}

export function childrenOf(pages: readonly TreePage[]): Map<string | null, TreePage[]> {
  const map = new Map<string | null, TreePage[]>();

  for (const page of pages) {
    const siblings = map.get(page.parentId) ?? [];
    siblings.push(page);
    map.set(page.parentId, siblings);
  }

  for (const siblings of map.values()) siblings.sort(byPosition);
  return map;
}

/**
 * The rows the sidebar actually renders: a depth-first walk that does not descend into a
 * collapsed node. A collapsed subtree is not in the DOM at all — not hidden, absent.
 *
 * This flat array is also what the keyboard needs (`↓`/`↑` move between VISIBLE rows),
 * and it is the exact input a virtualizer would take on the day we need one (`060` §8).
 * That day is not v1.
 */
export function visibleRows(
  pages: readonly TreePage[],
  expanded: ReadonlySet<string>,
): VisibleRow[] {
  const children = childrenOf(pages);
  const rows: VisibleRow[] = [];

  const walk = (parentId: string | null, depth: number): void => {
    for (const page of children.get(parentId) ?? []) {
      const kids = children.get(page.id) ?? [];
      const hasChildren = page.hasChildren || kids.length > 0;

      rows.push({ page, depth, hasChildren });
      if (hasChildren && expanded.has(page.id)) walk(page.id, depth + 1);
    }
  };

  walk(null, 0);
  return rows;
}

/** Every id in `pageId`'s subtree, INCLUDING itself — the invalid drop-target set (§2). */
export function subtreeIds(pages: readonly TreePage[], pageId: string): Set<string> {
  const children = childrenOf(pages);
  const ids = new Set<string>([pageId]);

  const walk = (id: string): void => {
    for (const child of children.get(id) ?? []) {
      ids.add(child.id);
      walk(child.id);
    }
  };

  walk(pageId);
  return ids;
}

/** Ancestors of a page, root-first. Used to auto-expand on navigation (§5). */
export function ancestorIds(pages: readonly TreePage[], pageId: string): string[] {
  const byId = new Map(pages.map((p) => [p.id, p]));
  const out: string[] = [];

  let current = byId.get(pageId)?.parentId ?? null;
  while (current !== null) {
    out.unshift(current);
    current = byId.get(current)?.parentId ?? null;
  }
  return out;
}

/* ── Positions ──────────────────────────────────────────────────────────────── */

export type DropZone = 'before' | 'after' | 'inside';

/**
 * The pointer's Y within the hovered row decides the intent (§5, "Drag and drop"):
 * top 25% → above, bottom 25% → below, middle 50% → inside, as the last child.
 */
export function dropZoneFor(offsetY: number, height: number): DropZone {
  if (height <= 0) return 'inside';
  const ratio = offsetY / height;
  if (ratio < 0.25) return 'before';
  if (ratio > 0.75) return 'after';
  return 'inside';
}

export interface Placement {
  parentId: string | null;
  position: string;
}

/**
 * Where a drop lands. **Never renumber siblings** — compute one key strictly between the
 * two neighbours and write that one string to that one row (§1). Insert, reorder and
 * re-parent are all a ONE-ROW update, always, no matter how many siblings exist.
 */
export function placementFor(
  pages: readonly TreePage[],
  targetId: string,
  zone: DropZone,
  movingId?: string,
): Placement | null {
  const target = pages.find((p) => p.id === targetId);
  if (target === undefined) return null;

  const children = childrenOf(pages);

  if (zone === 'inside') {
    // Appended as the LAST child of the target.
    const siblings = (children.get(target.id) ?? []).filter((p) => p.id !== movingId);
    const last = siblings.at(-1)?.position ?? null;
    return { parentId: target.id, position: generateKeyBetween(last, null) };
  }

  const siblings = (children.get(target.parentId) ?? []).filter((p) => p.id !== movingId);
  const index = siblings.findIndex((p) => p.id === target.id);
  if (index === -1) {
    // The target was the page being moved; it has been filtered out of its own sibling
    // list. Append to the end of that parent rather than guessing.
    const last = siblings.at(-1)?.position ?? null;
    return { parentId: target.parentId, position: generateKeyBetween(last, null) };
  }

  const before = zone === 'before' ? siblings[index - 1] : siblings[index];
  const after = zone === 'before' ? siblings[index] : siblings[index + 1];

  return {
    parentId: target.parentId,
    position: generateKeyBetween(before?.position ?? null, after?.position ?? null),
  };
}

/** A new page appended to the end of a parent's children. */
export function appendPosition(pages: readonly TreePage[], parentId: string | null): string {
  const siblings = childrenOf(pages).get(parentId) ?? [];
  return generateKeyBetween(siblings.at(-1)?.position ?? null, null);
}

/** A new page immediately AFTER `siblingId` — `Mod+N`'s placement (§6). */
export function positionAfter(pages: readonly TreePage[], siblingId: string): Placement {
  const sibling = pages.find((p) => p.id === siblingId);
  if (sibling === undefined) return { parentId: null, position: appendPosition(pages, null) };

  const siblings = childrenOf(pages).get(sibling.parentId) ?? [];
  const index = siblings.findIndex((p) => p.id === siblingId);
  const next = siblings[index + 1];

  return {
    parentId: sibling.parentId,
    position: generateKeyBetween(sibling.position, next?.position ?? null),
  };
}
